import { entries, isEmpty, keyBy, mapValues } from "lodash";
import { CalendarMode } from "PFApp/booking/types";
import { Row, RowType } from "PFApp/booking/types/row";
import { useEffect } from "react";

import { useBookingRowsContext } from "../../context/booking_rows_context";
import { useBookingsOverviewContext } from "../../context/bookings_overview_context";
import { useParentsAndChildrenMode } from "./use_parents_and_children_mode";

export interface AllBookingsModeChange {
  isAllBookingsModeEnabled: boolean;
  propagate?: boolean;
}

interface UseAllBookingsMode {
  row: Row;
  onAllBookingsModeEnable: VoidFunction;
  childrenCount: number;
}

interface UseAllBookingsModeReturn {
  isAllBookingsModeEnabled: boolean;
  handleAllBookingsModeChange: (data: AllBookingsModeChange) => void;
}

export const useAllBookingsMode = ({
  row,
  onAllBookingsModeEnable,
  childrenCount
}: UseAllBookingsMode): UseAllBookingsModeReturn => {
  const { calendarMode } = useBookingsOverviewContext();
  const { setAllBookingsModeRowsState } = useBookingRowsContext();
  const { key, type, parents } = row;

  const { isModeForAllChildrenEnabled, isAllBookingsModeEnabled } = useParentsAndChildrenMode({
    row,
    childrenCount
  });

  useEffect(() => {
    if (calendarMode !== CalendarMode.Projects) {
      return;
    }
    if (isAllBookingsModeEnabled) {
      handleAllBookingsModeChange({ isAllBookingsModeEnabled: true });
    }
  }, [isAllBookingsModeEnabled]);

  useEffect(() => {
    if (calendarMode !== CalendarMode.Projects || ![RowType.Engagement, RowType.Role].includes(type)) {
      return;
    }
    if (isModeForAllChildrenEnabled) {
      updateModeForCurrentRow({ isAllBookingsModeEnabled: true });
    }
  }, [isModeForAllChildrenEnabled]);

  const updateModeForCurrentRow = ({ isAllBookingsModeEnabled, propagate = true }: AllBookingsModeChange) => {
    if (propagate) {
      setAllBookingsModeRowsState((prevState) =>
        entries({ ...prevState, [key]: isAllBookingsModeEnabled }).reduce(
          (result, [currentKey, currentValue]) => ({
            ...result,
            [currentKey]: currentKey.includes(key) ? isAllBookingsModeEnabled : currentValue
          }),
          {}
        )
      );
    } else {
      setAllBookingsModeRowsState((prevState) => ({
        ...prevState,
        [key]: isAllBookingsModeEnabled
      }));
    }
  };

  const handleAllBookingsModeChange = ({ isAllBookingsModeEnabled, propagate }: AllBookingsModeChange) => {
    isAllBookingsModeEnabled && onAllBookingsModeEnable();
    updateModeForCurrentRow({ isAllBookingsModeEnabled, propagate });
    if ([RowType.Role, RowType.Profile].includes(type) && !isAllBookingsModeEnabled && !isEmpty(parents)) {
      const parentsWithDisabledState = mapValues(
        keyBy(parents, (key) => key),
        () => false
      );
      setAllBookingsModeRowsState((prevState) => ({
        ...prevState,
        ...parentsWithDisabledState
      }));
    }
  };

  return {
    isAllBookingsModeEnabled,
    handleAllBookingsModeChange
  };
};
